// src/pages/LandingPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, ArrowRight, Users, 
  ChevronRight, Award, Zap, 
  Search, Check, Shield, BarChart2, 
  ArrowUpRight, Star, Play, Menu, X,
  Clock, FileText, Cpu, Building2, Target, TrendingUp
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import BacktestingChart from '../components/charts/BacktestingChart';
import SEOHead from '../components/SEO/SEOHead';
import PortfolioComparisonImage from '../components/PortfolioComparisonImage';
import PortfolioComparisonTable from '../components/PortfolioComparisonTable';

// Removed dark mode functionality

function LandingPage() {
  const landingPageStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://pricepatent.com/#organization",
        "name": "PricePatent",
        "url": "https://pricepatent.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://pricepatent.com/logo.svg"
        },
        "description": "AI-powered platform for instant patent and company valuations. Get accurate, data-driven insights in seconds.",
        "email": "pricepatent@wisdomain.com"
      },
      {
        "@type": "WebSite",
        "@id": "https://pricepatent.com/#website",
        "url": "https://pricepatent.com",
        "name": "PricePatent - Patent Valuation Platform",
        "description": "Instant AI-powered patent valuations and IP analytics for professionals and investors",
        "publisher": {
          "@id": "https://pricepatent.com/#organization"
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://pricepatent.com/demo?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "SoftwareApplication",
        "name": "PricePatent Platform",
        "description": "AI-powered patent valuation and IP analytics software for instant, accurate patent assessments and alternative data insights",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web Browser",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "description": "Free demo available"
        },
        "featureList": [
          "Instant patent valuation",
          "IP portfolio analysis", 
          "Patent licensing insights",
          "Patent monetization strategies",
          "Alternative data for investments",
          "Market comparables analysis"
        ],
        "audience": {
          "@type": "Audience",
          "audienceType": ["IP Professionals", "Patent Attorneys", "Investors", "Technology Companies", "R&D Directors"]
        }
      },
      {
        "@type": "Service",
        "name": "Patent Valuation Service",
        "description": "Professional patent valuation and IP analytics using AI-powered algorithms and comprehensive market data",
        "provider": {
          "@id": "https://pricepatent.com/#organization"
        },
        "serviceType": "Patent Valuation",
        "category": "Intellectual Property Analytics",
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Patent Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Patent Valuation",
                "description": "AI-powered patent valuation with market analysis"
              }
            },
            {
              "@type": "Offer", 
              "itemOffered": {
                "@type": "Service",
                "name": "IP Analytics",
                "description": "Comprehensive intellectual property analytics and insights"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service", 
                "name": "Patent Licensing Analysis",
                "description": "Patent licensing potential and monetization strategies"
              }
            }
          ]
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SEOHead
        title="PricePatent - AI-Powered Patent Valuation | Instant IP Analytics"
        description="Get instant, AI-powered patent valuations and IP analytics. Professional patent licensing insights, monetization strategies, and alternative data for IP professionals and investors. Try our free demo."
        keywords="patent valuation, IP analytics, patent licensing, patent monetization, alternative data, intellectual property valuation, patent appraisal, IP investment, patent portfolio analysis, patent assessment, IP due diligence"
        ogTitle="PricePatent - Instant AI Patent Valuation Platform"
        ogDescription="Revolutionary AI-powered patent valuation platform. Get instant, accurate patent assessments, licensing insights, and IP analytics for professionals and investors."
        ogType="website"
        structuredData={landingPageStructuredData}
      />
      <CustomStyles />
      <Navbar />
      <main>
        <HeroSection />
        <LogoCloud />
        <StatsSection />
        <FeatureSection />
        <HowItWorksSection />
        {/* Data Categories Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
              Comprehensive Data Sources
            </h2>
            <p className="mt-4 text-xl text-gray-600">
              Access a wide range of data points for accurate patent and company valuations
            </p>
          </div>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-2">
            {[
              {
                title: "Patent Data",
                description: "Comprehensive patent information including technical specifications, legal status, and historical data.",
                icon: FileText,
                features: [
                  "Technical specifications and claims",
                  "Legal status and maintenance history",
                  "Citation networks and family data",
                  "Assignee and inventor information",
                  "Geographic coverage and filing dates"
                ]
              },
              {
                title: "Company Data",
                description: "Detailed company information including financial metrics, patent portfolios, and market position.",
                icon: Building2,
                features: [
                  "Financial statements and metrics",
                  "Patent portfolio analysis",
                  "Market position and competitors",
                  "Industry classification",
                  "Geographic presence"
                ]
              },
              {
                title: "Market Data",
                description: "Industry-specific market data including transaction history, licensing deals, and market trends.",
                icon: BarChart2,
                features: [
                  "Patent transaction history",
                  "Licensing deal data",
                  "Market size and growth",
                  "Industry benchmarks",
                  "Competitive landscape"
                ]
              },
              {
                title: "Valuation Metrics",
                description: "Comprehensive valuation metrics and methodologies for accurate patent and company valuation.",
                icon: Target,
                features: [
                  "Income method calculations",
                  "Market comparables",
                  "Cost-based analysis",
                  "Risk assessment metrics",
                  "Technology adoption rates"
                ]
              }
            ].map((category, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow duration-300">
                <div className="flex items-start space-x-4 mb-4">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
                    <category.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{category.title}</h3>
                </div>
                <p className="text-gray-600 mb-4">{category.description}</p>
                <ul className="space-y-2">
                  {category.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <div className="w-5 h-5 bg-green-50 rounded-full flex items-center justify-center mr-3">
                        <Check className="h-3 w-3 text-green-600" />
                      </div>
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <CtaSection />
      </main>
    </div>
  );
}

// Custom header component
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 w-full z-50 backdrop-blur-lg bg-white/80 border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <span className="flex items-center font-bold text-2xl text-gray-900">
              <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">Price</span>Patent
            </span>
            <nav className="hidden md:ml-10 md:flex md:space-x-8">
              <Link className="text-gray-500 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors duration-300" to="/features">Features</Link>
              <Link className="text-gray-500 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors duration-300" to="/pricing">Pricing</Link>
              <Link className="text-gray-500 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors duration-300" to="/about">About</Link>
              <Link className="text-gray-500 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors duration-300" to="/dashboard">Try Demo</Link>
              <Link className="text-gray-500 hover:text-gray-900 px-3 py-2 text-sm font-medium transition-colors duration-300" to="/sample-report">Sample Report</Link>
            </nav>
          </div>
          <div className="flex items-center">
            <div className="hidden md:ml-6 md:flex md:items-center md:space-x-4">
              <Link to="/login" className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors duration-300">Log in</Link>
              <Link to="/signup" className="px-4 py-2 text-sm font-medium rounded-lg bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 text-white transition-all duration-300">Sign up</Link>
            </div>
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-900 transition-colors duration-300"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-b border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link className="block px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors duration-300" to="/features">Features</Link>
            <Link className="block px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors duration-300" to="/pricing">Pricing</Link>
            <Link className="block px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors duration-300" to="/about">About</Link>
            <Link className="block px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors duration-300" to="/dashboard">Try Demo</Link>
            <Link className="block px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors duration-300" to="/sample-report">Sample Report</Link>
            <Link className="block px-3 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors duration-300" to="/login">Log in</Link>
            <Link className="block px-3 py-2 text-base font-medium bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 text-white rounded-md transition-all duration-300" to="/signup">Sign up</Link>
          </div>
        </div>
      )}
    </header>
  );
};

const HeroSection = () => {
  return (
    <section className="relative overflow-visible min-h-screen flex items-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 pt-32 md:pt-20 pb-24">
      {/* Gradient elements */}
      <div className="absolute top-0 right-0 opacity-70 md:opacity-70 opacity-50 pointer-events-none" style={{ transform: 'translate(30%, -30%) md:translateY(-10%)' }}>
        <svg width="min(600px, 60vw)" height="min(600px, 60vw)" viewBox="0 0 800 800" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="400" cy="400" r="400" fill="url(#paint0_radial_1_2)" />
          <defs>
            <radialGradient id="paint0_radial_1_2" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(400 400) rotate(90) scale(400)">
              <stop stopColor="#2563EB" />
              <stop offset="1" stopColor="#2563EB" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </div>
      
      <div className="absolute bottom-0 left-0 opacity-70 md:opacity-70 opacity-50 pointer-events-none" style={{ transform: 'translate(-20%, 30%) md:translateY(20%)' }}>
        <svg width="min(400px, 40vw)" height="min(400px, 40vw)" viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="250" cy="250" r="250" fill="url(#paint0_radial_2_3)" />
          <defs>
            <radialGradient id="paint0_radial_2_3" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(250 250) rotate(90) scale(250)">
              <stop stopColor="#6366F1" />
              <stop offset="1" stopColor="#6366F1" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tight leading-tight">
            <span className="block pb-1">Instant</span>
            <span className="block mt-2 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent pb-1">
              Patent Valuations
            </span>
          </h1>
          <p className="mt-6 text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            AI-powered valuations in seconds, not weeks
          </p>
          
          {/* Animated Portfolio Comparison Table */}
          <PortfolioComparisonTable />

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/demo" className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-lg shadow-lg text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 animate-bounce-subtle">
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/sample-report"
              className="inline-flex items-center justify-center px-8 py-3 text-base font-medium rounded-lg shadow-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-300"
            >
              <FileText className="mr-2 h-5 w-5" />
              View Samples
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatsSection = () => (
  <section className="relative py-16 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
        <div className="text-center">
          <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">377M+</div>
          <div className="mt-2 text-sm text-gray-600">Patents Analyzed</div>
        </div>
        <div className="text-center">
          <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">2.1 sec</div>
          <div className="mt-2 text-sm text-gray-600">Average Report Time</div>
        </div>
        <div className="text-center">
          <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">30+</div>
          <div className="mt-2 text-sm text-gray-600">Valuation Factors</div>
        </div>
        <div className="text-center">
          <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">220K+</div>
          <div className="mt-2 text-sm text-gray-600">Companies Valuated</div>
        </div>
      </div>
    </div>
  </section>
);

const LogoCloud = () => {
  // Custom spacing classes for specific logos
  const logoStyles = {
    samsung: "px-2 lg:px-4 scale-85",
    sony: "px-4 lg:px-6 scale-90",
    hitachi: "px-6 lg:px-8 scale-100",
    lg: "px-6 lg:px-8 scale-100",
    panasonic: "px-4 lg:px-6 scale-100",
    brother: "px-2 lg:px-4 scale-85",
    
    hyundai: "px-4 lg:px-6 scale-85",
    mitsubishi: "px-6 lg:px-8 scale-100",
    ntt: "px-6 lg:px-8 scale-90",
    hanwha: "px-6 lg:px-8 scale-90",
    asahikasei: "px-4 lg:px-6 scale-85",
    keyence: "px-4 lg:px-6 scale-90",
    
    "tokyo-university": "px-6 lg:px-8 scale-70",
    "kyoto-university": "px-6 lg:px-8 scale-70",
    "osaka-university": "px-6 lg:px-8 scale-70",
    snu: "px-4 lg:px-6 scale-85",
    kaist: "px-6 lg:px-8 scale-90",
    kiip: "px-6 lg:px-8 scale-90",
    
    kriss: "px-4 lg:px-6 scale-85",
    kista: "px-6 lg:px-8 scale-90",
    mri: "px-6 lg:px-8 scale-90",
    ibk: "px-4 lg:px-6 scale-85",
    sel: "px-6 lg:px-8 scale-80",
    mext: "px-4 lg:px-6 scale-100"
  };

  const companies = [
    // Row 1 - Global Tech Giants
    { name: "Samsung", logo: "/images/companies/samsung" },
    { name: "Sony", logo: "/images/companies/sony" },
    { name: "Hitachi", logo: "/images/companies/hitachi" },
    { name: "LG", logo: "/images/companies/lg" },
    { name: "Panasonic", logo: "/images/companies/panasonic" },
    { name: "Brother", logo: "/images/companies/brother" },

    // Row 2 - Major Industrial & Auto
    { name: "Hyundai", logo: "/images/companies/hyundai" },
    { name: "Mitsubishi", logo: "/images/companies/mitsubishi" },
    { name: "NTT", logo: "/images/companies/ntt" },
    { name: "Hanwha", logo: "/images/companies/hanwha" },
    { name: "AsahiKASEI", logo: "/images/companies/asahi-kasei" },
    { name: "Keyence", logo: "/images/companies/keyence" },

    // Row 3 - Top Universities
    { name: "Tokyo University", logo: "/images/companies/tokyo-university" },
    { name: "Kyoto University", logo: "/images/companies/kyoto-university" },
    { name: "Osaka University", logo: "/images/companies/osaka-university" },
    { name: "Seoul National University", logo: "/images/companies/snu" },
    { name: "KAIST", logo: "/images/companies/kaist" },
    { name: "KIIP", logo: "/images/companies/kiip" },

    // Row 4 - Research & Financial Institutions
    { name: "KRISS", logo: "/images/companies/kriss" },
    { name: "KISTA", logo: "/images/companies/kista" },
    { name: "MRI", logo: "/images/companies/mri" },
    { name: "IBK", logo: "/images/companies/ibk" },
    { name: "SEL", logo: "/images/companies/sel" },
    { name: "MEXT", logo: "/images/companies/mext" }
  ];

  const imageFormats = ['.svg', '.png', '.jpg', '.webp'];

  return (
    <section className="relative py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-center text-sm font-semibold text-gray-500 uppercase tracking-wide mb-12">
          TRUSTED BY INNOVATIVE COMPANIES WORLDWIDE
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-12 gap-y-16">
          {companies.map((company, index) => (
            <div key={company.name} className={`flex items-center justify-center ${index >= 24 ? 'lg:col-start-3 lg:col-span-2' : ''}`}>
              <div className={`h-14 relative flex items-center justify-center group w-full transition-transform duration-300 ${
                logoStyles[company.name.toLowerCase().replace(/\s+/g, '-')]
              }`}>
                {imageFormats.map((format, idx) => (
                  <img
                    key={format}
                    src={`${company.logo}${format}`}
                    alt={`${company.name} logo`}
                    className={`h-full w-auto max-w-[160px] object-contain [filter:grayscale(50%)] opacity-75 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300 ${
                      idx > 0 ? 'hidden' : ''
                    }`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      const nextImage = e.target.nextElementSibling;
                      if (nextImage && nextImage.tagName === 'IMG') {
                        nextImage.classList.remove('hidden');
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-20 mb-8">
          <p className="text-gray-400 text-sm font-medium tracking-wider">
            and many more leading institutions...
          </p>
          <div className="mt-6 flex justify-center space-x-2">
            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
          </div>
        </div>
      </div>
    </section>
  );
};

const FeatureSection = () => (
  <section className="relative py-24 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
    <div className="absolute left-1/2 transform -translate-x-1/2 bottom-0 pointer-events-none" aria-hidden="true">
      <svg width="1360" height="578" viewBox="0 0 1360 578" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="illustration-01">
            <stop stopColor="#2563EB" stopOpacity=".05" offset="0%" />
            <stop stopColor="#2563EB" stopOpacity="0" offset="100%" />
          </linearGradient>
        </defs>
        <g fill="url(#illustration-01)" fillRule="evenodd">
          <circle cx="1232" cy="128" r="128" />
          <circle cx="155" cy="443" r="64" />
        </g>
      </svg>
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <div className="text-center max-w-3xl mx-auto mb-16">
        <h2 className="text-base font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent uppercase tracking-wide">Revolutionary Features</h2>
        <p className="mt-2 text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
          Automatic IP valuation in seconds
        </p>
        <p className="mt-5 text-xl text-gray-600">
          Our AI-powered platform automatically analyzes patents and companies to deliver instant, accurate valuations and detailed reports.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-y-12 gap-x-6 md:grid-cols-2 lg:grid-cols-3">
        <Feature 
          icon={<Clock className="h-8 w-8 text-white" />}
          color="bg-gradient-to-br from-blue-600 to-sky-500"
          title="Real-Time Valuation"
          description="Generate comprehensive patent and company valuations in seconds, not days or weeks, with our advanced AI algorithms."
        />
        <Feature 
          icon={<FileText className="h-8 w-8 text-white" />}
          color="bg-gradient-to-br from-purple-600 to-pink-500"
          title="Automatic Reports"
          description="Instantly generate detailed valuation reports with market analysis, comparable assets, and growth projections."
        />
        <Feature 
          icon={<Cpu className="h-8 w-8 text-white" />}
          color="bg-gradient-to-br from-emerald-600 to-teal-500"
          title="AI-Powered Analysis"
          description="Our proprietary AI analyzes 30+ factors including market trends, legal strength, citation networks, and technology relevance."
        />
        <Feature 
          icon={<BarChart2 className="h-8 w-8 text-white" />}
          color="bg-gradient-to-br from-amber-600 to-orange-500"
          title="Dynamic Market Updates"
          description="Valuations update in real-time as market conditions change, ensuring you always have the most current IP assessment."
        />
        <Feature 
          icon={<Shield className="h-8 w-8 text-white" />}
          color="bg-gradient-to-br from-rose-600 to-red-500"
          title="Competitive Intelligence"
          description="Automatically benchmark your IP assets against competitors and identify strategic opportunities for growth."
        />
        <Feature 
          icon={<Search className="h-8 w-8 text-white" />}
          color="bg-gradient-to-br from-indigo-600 to-violet-500"
          title="Portfolio Scanning"
          description="Instantly analyze your entire IP portfolio to identify strengths, weaknesses, and unexplored opportunities."
        />
      </div>
    </div>
  </section>
);

const Feature = ({ icon, color, title, description }) => (
  <div className="relative">
    <div className={`absolute h-12 w-12 rounded-xl ${color} flex items-center justify-center`}>
      {icon}
    </div>
    <div className="pl-16">
      <h3 className="text-xl font-bold text-gray-900">{title}</h3>
      <p className="mt-2 text-base text-gray-500">{description}</p>
    </div>
  </div>
);

const HowItWorksSection = () => (
  <section className="py-16 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-gray-900">How It Works</h2>
        <p className="mt-4 text-xl text-gray-600">Get your patent valuation in three simple steps</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-blue-50 mb-4">
            <Search className="h-6 w-6 text-blue-600" />
      </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Enter Patent Details</h3>
          <p className="text-gray-600">Provide your patent number or upload patent documents. Our AI will analyze the technical specifications and claims.</p>
    </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-blue-50 mb-4">
            <Cpu className="h-6 w-6 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">2. AI Analysis</h3>
          <p className="text-gray-600">Our advanced AI evaluates market potential, technical novelty, and competitive landscape to determine value.</p>
    </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-blue-50 mb-4">
            <FileText className="h-6 w-6 text-blue-600" />
    </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Get Your Report</h3>
          <p className="text-gray-600">Receive a comprehensive valuation report with market analysis, comparable patents, and value range in seconds.</p>
  </div>
      </div>
      
      <div className="mt-12 text-center">
        <Link to="/demo" className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-md text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 animate-bounce-subtle">
          Try It Now
          <ArrowRight className="ml-2 h-5 w-5" />
        </Link>
      </div>
    </div>
  </section>
);

const CtaSection = () => (
  <section className="relative py-16 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
        Ready to get started?
      </h2>
      <p className="mt-4 text-lg text-blue-100 max-w-2xl mx-auto">
        Try our demo and get your first valuation in seconds.
      </p>
      <div className="mt-8">
        <Link 
          to="/demo" 
          className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-blue-600 bg-white hover:bg-blue-50 transition-colors duration-300"
        >
          Try Demo Now
          <ArrowRight className="ml-2 h-5 w-5" />
        </Link>
      </div>
    </div>
  </section>
);

// Custom CSS for styling
const CustomStyles = () => {
  useEffect(() => {
    // Add CSS to head for global styling
    const style = document.createElement('style');
    style.textContent = `
      .aspect-w-16 {
        position: relative;
        padding-bottom: 56.25%;
      }
      
      .aspect-w-16 > * {
        position: absolute;
        height: 100%;
        width: 100%;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
      }
      
      .animate-pulse {
        animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
      }
      
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
        }
        50% {
          opacity: .5;
        }
      }

      .animate-bounce-subtle {
        animation: bounce-subtle 2s infinite;
      }

      @keyframes bounce-subtle {
        0%, 100% {
          transform: translateY(0);
        }
        50% {
          transform: translateY(-5px);
        }
      }
      
      @media (min-width: 640px) {
        .sm\\:text-4xl {
          font-size: 2.25rem;
          line-height: 2.5rem;
        }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  return null;
};

export default LandingPage;